import { memo, useMemo } from 'react';

import { useTranslation } from '@mint-lib/i18n';

import {
  Box,
  Dialog,
  StaticNumericSpan,
  Table,
  TableBody,
  TableBodyCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Typography,
} from '@myn/mui';

import ConditionalCurrencyField from '../../atoms/ConditionalCurrencyField.jsx';
import type { Currency } from '../../types/_campaigns/campaigns.js';

export type Mode = 'confirm' | 'reject';

type ModalContent = {
  header: string;
  subtitle: string;
  confirmLabel: string;
  description?: string;
};
export type Props = {
  mode: Mode | null;
  co2ToOffset: string;
  offsetCost: string;
  currency: Currency | null;
  onClose: () => void;
  onSave: () => void;
};

const NewCo2Modal = ({
  mode,
  co2ToOffset,
  offsetCost,
  currency,
  onClose,
  onSave,
}: Props) => {
  const { t } = useTranslation('@myn/planning');

  const mapModeToContent: Record<Mode, ModalContent> = useMemo(
    () => ({
      confirm: {
        header: t('Payment confirmation'),
        subtitle: t(
          'By accepting, you agree to offset the CO2 emissions generated by your Buying Plan. The offset cost will be added to your invoice.',
        ),
        description: t('Service provided in collaboration with ClimatePartner'),
        confirmLabel: t('Pay the offset'),
      },
      reject: {
        header: t('Reject this payment?'),
        subtitle: t(
          'By rejecting, you choose not to offset the CO2 emissions generated by your Buying Plan. The offset cost will not be added to your invoice.',
        ),
        confirmLabel: t('Yes, reject the offset'),
      },
    }),
    [],
  );

  if (!mode) {
    return null;
  }
  return (
    <Dialog
      title={mapModeToContent[mode].header}
      open={Boolean(mode.length)}
      cancelButtonProps={{
        'aria-label': 'cancel',
        label: t('Cancel'),
        onClick: onClose,
      }}
      submitButtonProps={{
        'aria-label': mapModeToContent[mode].confirmLabel,
        label: mapModeToContent[mode].confirmLabel,
        onClick: onSave,
        color: mode === 'confirm' ? 'primary' : 'error',
      }}
      onClose={onClose}
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        gap={(theme) => theme.spacing(3)}
      >
        {mapModeToContent[mode].subtitle}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>
                  <Typography variant="h6" color="textPrimary">
                    {t('CO2 to offset')}
                  </Typography>
                </TableHeaderCell>
                <TableHeaderCell>
                  <Typography variant="h6" color="textPrimary">
                    {t('Offset cost')}
                  </Typography>
                </TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableBodyCell>
                  <Typography variant="body1" color="textPrimary">
                    <StaticNumericSpan
                      number={co2ToOffset}
                      options={{
                        prefix: ' Kg',
                        minDecimals: 2,
                      }}
                    />
                  </Typography>
                </TableBodyCell>
                <TableBodyCell>
                  <Typography variant="body1" color="textPrimary">
                    <ConditionalCurrencyField
                      currency={currency}
                      value={offsetCost}
                      decimals={2}
                    />
                  </Typography>
                </TableBodyCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Dialog>
  );
};

export default memo(NewCo2Modal);
